import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, Input, Select, Badge } from '../ui-v2.5';
import { Button } from '../ui-v2';
import {
  Loader2, AlertCircle, AlertTriangle, KeyRound, Package, CheckCircle2, ExternalLink, Info,
  UserX, CalendarClock,
} from 'lucide-react';
import { queTienePersona, bajaAbiertaDePersona, crearBaja, listMotivosBaja, listAreas } from './api';
import { fmtFecha } from './fecha';
import { QuienSeEntera } from './piezasProceso';

/**
 * CrearBajaDialog — EL MISMO componente para los dos caminos (Fase 11 §4.5).
 *
 * Se abre desde la pestaña de Bajas ("Nueva baja") y desde la ficha de la persona, cuando su
 * estado pasa a "Baja". **Dos caminos, un resultado**: si fueran dos diálogos parecidos, el día
 * que cambie una regla —un campo más, otro guard— se actualizaría uno y no el otro, y las bajas
 * creadas por un camino saldrían distintas de las del otro sin que nadie lo viera.
 *
 * FUNCIONA EN LOS DOS EJES. `ambito` es 'interno' o 'colaborador' y viaja a la ruta; la baja se
 * crea sobre la tabla del titular.
 *
 * LO QUE ENSEÑA ANTES DE CONFIRMAR: qué credenciales y qué material tiene la persona, porque de
 * ahí salen las tareas. Y en el caso no fiable dice la verdad **con el mensaje del backend, tal
 * cual** — ver `SinFiabilidad` más abajo, que es donde está el motivo de no reescribirlo aquí.
 *
 * Y QUIÉN SE VA A ENTERAR (14/10): las áreas que tienen tarea, que no se pueden quitar, y las
 * áreas y correos que se añaden a mano, que se guardan con la baja.
 */

const hoyISO = () => {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};
const cuerpoError = (e) => (e && e.response && e.response.data) || {};

/**
 * El aviso de material no fiable. Se pinta el texto que MANDA EL BACKEND (`motivo_no_fiable`) y
 * no uno propio: el motivo es un defecto de integridad concreto —la clave ajena de Inventario
 * apunta a `personas`, y una colaboradora que no esté ahí no puede tener material registrado—, y
 * quien lo conoce es el servicio. Una copia en el cliente se quedaría desfasada el día que el
 * defecto se arregle, y seguiría asustando a quien lea la pantalla.
 *
 * La distinción que sostiene todo esto: **"no tiene" y "no lo sabemos" no son lo mismo.** Una
 * lista vacía presentada sin más sería una mentira tranquilizadora.
 */
function SinFiabilidad({ motivo }) {
  return (
    <div className="flex gap-2 rounded-v25-lg border border-amber-200 bg-amber-50 px-3 py-2.5">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
      <div className="min-w-0">
        <p className="text-xs font-semibold text-amber-900">No se puede saber qué material tiene</p>
        <p className="mt-0.5 text-xs leading-relaxed text-amber-800">{motivo}</p>
      </div>
    </div>
  );
}

function Lista({ icon: Icon, titulo, vacio, children, n }) {
  return (
    <div>
      <div className="mb-1.5 flex items-center gap-1.5">
        <Icon className="h-3.5 w-3.5 text-v25-ink-400" />
        <span className="text-xs font-semibold text-v25-ink-700">{titulo}</span>
        {n > 0 && <span className="text-xs text-v25-ink-400">({n})</span>}
      </div>
      {n > 0 ? <ul className="space-y-1">{children}</ul>
        : <p className="text-xs text-v25-ink-400">{vacio}</p>}
    </div>
  );
}

export default function CrearBajaDialog({
  open, ambito, personaId, personaNombre, onClose, onCreada, onAbrirBaja,
}) {
  const [cargando, setCargando] = useState(true);
  const [errorCarga, setErrorCarga] = useState(null);
  const [abierta, setAbierta] = useState(null);   // baja ya existente, si la hay
  const [tiene, setTiene] = useState(null);
  const [fecha, setFecha] = useState(hoyISO());
  /* EL MOTIVO SE ELIGE, YA NO SE ESCRIBE. Esta era la puerta que reponia los pendientes: cada
   * baja creada aqui nacia con texto libre y sin `motivo_id`, asi que la migracion que retira la
   * columna de texto no habria convergido nunca. */
  const [motivoId, setMotivoId] = useState('');
  const [motivos, setMotivos] = useState([]);
  /* ── EL MISMO CATCH MUDO QUE EN `EditarBajaDialog`, Y AQUI ERA PEOR ──────────────────────
   * Con `.catch(() => setMotivos([]))`, una peticion rechazada dejaba el desplegable vacio —
   * indistinguible de un catalogo vacio— y entonces NO SE PUEDE CREAR NINGUNA BAJA, porque el
   * motivo es obligatorio. Nadie lo habia encontrado porque el fallo hermano salio antes.
   * «Fallo la peticion» y «no hay motivos» tienen que verse distinto. */
  const [errorMotivos, setErrorMotivos] = useState(null);
  const [tocado, setTocado] = useState({ fecha: false, motivo: false });
  const [guardando, setGuardando] = useState(false);
  const [errorGuardar, setErrorGuardar] = useState(null);

  /* ── QUIÉN SE ENTERA, ELEGIDO AL CREAR (14/10) ──────────────────────────────────────────────
   * Lo mismo que «Quién se entera» en la pantalla de la baja, antes de que la baja exista:
   *   · las áreas con tarea vienen del servidor (`areas_previstas`), calculadas con las MISMAS
   *     tareas que va a crear la baja, y no se quitan: se enteran igual;
   *   · las áreas y los correos que se añaden aquí viajan con la creación y se guardan con ella.
   * Crear NO avisa a nadie: el aviso sale al cerrar la baja o con «Volver a avisar». */
  const [areasCatalogo, setAreasCatalogo] = useState([]);
  const [errorAreas, setErrorAreas] = useState(null);
  const [elegidas, setElegidas] = useState([]);   // ids de área
  const [correos, setCorreos] = useState([]);
  const [areaAAvisar, setAreaAAvisar] = useState('');
  const [correoAAvisar, setCorreoAAvisar] = useState('');
  const [errorCorreo, setErrorCorreo] = useState(null);

  const cargar = useCallback(async () => {
    setCargando(true); setErrorCarga(null); setAbierta(null); setTiene(null);
    try {
      // EL GUARD PRIMERO, y las dos peticiones en paralelo: si ya hay una baja abierta no se va a
      // poder crear otra, pero pedir las dos a la vez evita un salto visible en el diálogo.
      const [g, t] = await Promise.all([
        bajaAbiertaDePersona(ambito, personaId),
        queTienePersona(ambito, personaId),
      ]);
      if (g && g.tiene_baja_abierta) setAbierta(g.baja);
      setTiene(t);
    } catch (e) {
      console.error('[bajas][dialogo] cargar', e);
      setErrorCarga(cuerpoError(e).message || 'No se pudo consultar la situación de esta persona.');
    } finally {
      setCargando(false);
    }
  }, [ambito, personaId]);

  useEffect(() => {
    if (!open || !personaId) return;
    setFecha(hoyISO()); setMotivoId(''); setTocado({ fecha: false, motivo: false }); setErrorGuardar(null);
    setErrorMotivos(null);
    setElegidas([]); setCorreos([]); setAreaAAvisar(''); setCorreoAAvisar(''); setErrorCorreo(null);
    setErrorAreas(null);
    /* Si falla, se dice y se puede crear igual: añadir a alguien es opcional, y las áreas con
     * tarea se enteran de todas formas. Pero no se traga: un desplegable vacío por un 500 se lee
     * como «no hay áreas». */
    listAreas()
      .then((r) => setAreasCatalogo((Array.isArray(r) ? r : (r && r.areas) || []).filter((a) => a.activa !== false)))
      .catch((e) => {
        setAreasCatalogo([]);
        setErrorAreas((e && e.response && e.response.data && e.response.data.message)
          || 'No se ha podido cargar el catálogo de áreas. Puedes crear la baja y añadirlas después.');
      });
    listMotivosBaja()
      .then((d) => setMotivos((d && d.motivos) || []))
      .catch((e) => { setMotivos([]); setErrorMotivos((e && e.response && e.response.data && e.response.data.message) || 'No se ha podido cargar el catálogo de motivos. Cierra y vuelve a abrir.'); });
    cargar();
  }, [open, personaId, cargar]);

  const credPendientes = useMemo(
    () => (tiene?.credenciales || []).filter((c) => c.pendiente_revocar), [tiene]);
  const materialItems = tiene?.material?.items || [];
  // Las tareas que va a crear el backend: una por credencial sin revocar y una por activo en su
  // poder. Se anuncia el número ANTES de confirmar, que es lo que pide el brief.
  const nTareas = credPendientes.length + materialItems.length;

  // Los miembros salen del catálogo, para que cada área diga cuántas personas tiene: un área
  // vacía se tiene que ver antes de crear, no descubrirse porque el aviso no llegó a nadie.
  const conMiembros = useCallback((a) => {
    const c = areasCatalogo.find((x) => Number(x.id) === Number(a.id));
    return { ...a, miembros: (c && c.miembros) || [] };
  }, [areasCatalogo]);
  const derivadas = useMemo(
    () => (tiene?.areas_previstas || []).map(conMiembros), [tiene, conMiembros]);
  const elegidasObj = useMemo(
    () => elegidas.map((id) => areasCatalogo.find((a) => Number(a.id) === id)).filter(Boolean),
    [elegidas, areasCatalogo]);
  // En el desplegable no salen las que ya se enteran por su tarea: añadirlas no cambiaría nada.
  const catalogoLibre = useMemo(
    () => areasCatalogo.filter((a) => !derivadas.some((x) => Number(x.id) === Number(a.id))),
    [areasCatalogo, derivadas]);

  /* La forma del correo se comprueba aquí además de en el servidor: uno mal escrito no da error
   * al enviar, simplemente no llega. Es la misma comprobación que en la pantalla de la baja. */
  const anadirCorreo = () => {
    const v = correoAAvisar.trim().toLowerCase();
    if (!v) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) { setErrorCorreo('No parece una dirección de correo.'); return; }
    if (correos.includes(v)) { setErrorCorreo('Ese correo ya está en la lista.'); return; }
    setCorreos((l) => [...l, v]); setCorreoAAvisar(''); setErrorCorreo(null);
  };

  // ¿La fecha elegida ya ha llegado? Comparación de cadenas AAAA-MM-DD, que ordenan como fechas.
  // Es la MISMA pregunta que se hace el backend (`estadoPersona.fechaYaLlegada`), y de ella
  // depende lo único que este diálogo no puede callar: si la persona pierde el acceso HOY.
  const yaLlegada = !!fecha && fecha <= hoyISO();

  const faltaFecha = !fecha;
  const faltaMotivo = !motivoId;
  const puedeGuardar = !faltaFecha && !faltaMotivo && !guardando && !abierta;

  const guardar = async () => {
    setTocado({ fecha: true, motivo: true });
    if (faltaFecha || faltaMotivo) return;
    setGuardando(true); setErrorGuardar(null);
    try {
      const r = await crearBaja({
        tipo_persona: ambito, persona_id: personaId, motivo_id: Number(motivoId), fecha_baja: fecha,
        // La misma forma que «Editar áreas»: [{ area_id }, { email }]. Se guarda con la baja.
        destinatarios: [...elegidas.map((id) => ({ area_id: id })), ...correos.map((email) => ({ email }))],
      });
      onCreada && onCreada(r);
    } catch (e) {
      const c = cuerpoError(e);
      // `baja_ya_abierta` puede llegar aquí aunque el guard de arriba dijera que no: entre la
      // carga del diálogo y el envío, otra persona ha podido crearla. El servidor es la barrera
      // real, y el cliente se limita a enseñar bien lo que el servidor decida.
      if (c.error === 'baja_ya_abierta' && c.baja) { setAbierta(c.baja); setErrorGuardar(null); }
      else setErrorGuardar(c.message || 'No se pudo crear la baja.');
    } finally {
      setGuardando(false);
    }
  };

  // ── el guard 1 · ya hay un proceso abierto ────────────────────────────────────────────────
  // No se crea una segunda. Y se OFRECE ABRIRLA, en vez de dejar al usuario buscándola: el
  // objetivo de quien llega aquí es gestionar la salida, y ya hay dónde hacerlo.
  const yaExiste = !!abierta;

  const pie = cargando ? null : yaExiste ? (
    <div className="flex items-center justify-end gap-2">
      <Button variant="outline" onClick={onClose}>Cerrar</Button>
      {onAbrirBaja && (
        <Button onClick={() => onAbrirBaja(abierta)}>
          <ExternalLink />Abrir {abierta.numero}
        </Button>
      )}
    </div>
  ) : (
    <div className="flex items-center justify-end gap-2">
      <Button variant="outline" onClick={onClose} disabled={guardando}>Cancelar</Button>
      <Button onClick={guardar} disabled={!puedeGuardar}>
        {guardando ? <Loader2 className="animate-spin" /> : <CheckCircle2 />}
        {guardando ? 'Creando…' : yaLlegada ? 'Crear y desactivar' : 'Crear proceso de salida'}
      </Button>
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={guardando ? undefined : onClose}
      size="lg"
      title={yaExiste ? 'Esta persona ya tiene una baja abierta' : `Dar de baja a ${personaNombre || 'esta persona'}`}
      subtitle={yaExiste ? undefined
        : 'Se creará el proceso de salida con su número y sus tareas de devolución y revocación.'}
      footer={pie}
    >
      {cargando ? (
        <div className="px-1 py-10 text-center text-sm text-v25-ink-400">
          <Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" />
          Comprobando qué tiene esta persona…
        </div>
      ) : errorCarga ? (
        <div className="px-1 py-8 text-center">
          <AlertCircle className="mx-auto mb-2 h-6 w-6 text-red-500" />
          <p className="text-sm text-red-600">{errorCarga}</p>
          <Button variant="outline" className="mt-3" onClick={cargar}>Reintentar</Button>
        </div>
      ) : yaExiste ? (
        <div className="space-y-3">
          <div className="flex gap-2 rounded-v25-lg border border-v25-line bg-v25-info-bg px-3 py-2.5">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-v25-info-fg" />
            <div className="min-w-0 text-xs leading-relaxed text-v25-ink-700">
              No se crea una segunda. El proceso <strong className="font-semibold">{abierta.numero}</strong> ya
              está {abierta.estado === 'borrador' ? 'en borrador' : 'en curso'}
              {abierta.fecha_baja ? <> con fecha de baja <strong className="font-semibold">{fmtFecha(abierta.fecha_baja)}</strong></> : null}.
              {/* El servidor manda el nombre resuelto; `motivo` a secas era la columna de texto,
                  retirada el 02/10, y leerla aqui daba siempre `undefined` sin error ninguno. */}
              {abierta.motivo_nombre ? <> Motivo: <em>{abierta.motivo_nombre}</em>.</> : null}
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {/* LO QUE VA A PASAR CON SU ACCESO, antes de pedir nada y antes que nada.
              Desde §2.ter crear una baja con fecha de hoy o pasada DESACTIVA a la persona en el
              acto. Es el efecto más grave del botón y no puede quedar implícito. */}
          {yaLlegada ? (
            <div className="flex gap-2 rounded-v25-lg border border-red-200 bg-red-50 px-3 py-2.5">
              <UserX className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
              <div className="min-w-0">
                <p className="text-xs font-semibold text-red-900">
                  {personaNombre || 'Esta persona'} perderá el acceso a la intranet al confirmar
                </p>
                <p className="mt-0.5 text-xs leading-relaxed text-red-800">
                  La fecha de baja ya ha llegado, así que su cuenta se desactiva ahora mismo, junto
                  con la creación del proceso. Si te equivocas, <strong className="font-semibold">cancelar
                  la baja la reactiva</strong> y la deja como estaba.
                </p>
              </div>
            </div>
          ) : fecha ? (
            <div className="flex gap-2 rounded-v25-lg border border-v25-line bg-v25-info-bg px-3 py-2.5">
              <CalendarClock className="mt-0.5 h-4 w-4 shrink-0 text-v25-info-fg" />
              <p className="text-xs leading-relaxed text-v25-ink-700">
                La fecha aún no ha llegado: {personaNombre || 'esta persona'} <strong className="font-semibold">sigue
                trabajando y conserva su acceso</strong>. Se desactivará sola el {fecha.split('-').reverse().join('/')}.
              </p>
            </div>
          ) : null}

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Input
              label="Fecha de baja *"
              type="date"
              value={fecha}
              onChange={(e) => setFecha(e.target.value)}
              onBlur={() => setTocado((t) => ({ ...t, fecha: true }))}
              error={tocado.fecha && faltaFecha ? 'La fecha de baja es obligatoria.' : undefined}
            />
            <Select
              label="Motivo *"
              value={motivoId}
              onChange={(e) => setMotivoId(e.target.value)}
              onBlur={() => setTocado((t) => ({ ...t, motivo: true }))}
              error={tocado.motivo && faltaMotivo ? 'Elige el motivo en el desplegable.' : undefined}
            >
              <option value="">Seleccionar…</option>
              {/* Solo los ACTIVOS: un motivo desactivado deja de ofrecerse y el servidor lo rechaza
                  igual si llega por API. */}
              {motivos.filter((m) => m.activo).map((m) => (
                <option key={m.id} value={m.id}>{m.nombre}</option>
              ))}
            </Select>
            {/* LAS DOS CAUSAS, SEPARADAS. Sin el motivo no se puede crear la baja, asi que si el
                desplegable esta vacio hay que decir POR QUE y no dejar a nadie mirando. */}
            {errorMotivos && (
              <p className="mt-1 text-[12px] leading-relaxed text-red-600">{errorMotivos}</p>
            )}
            {!errorMotivos && motivos.length === 0 && (
              <p className="mt-1 text-[12px] leading-relaxed text-amber-800">
                No hay motivos en el catálogo, y el motivo es obligatorio. Créalos en
                <strong> Configuración → Motivos de baja</strong>.
              </p>
            )}
          </div>

          <div className="rounded-v25-lg border border-v25-line p-3">
            <div className="mb-2.5 flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wide text-v25-ink-500">Qué tiene ahora</span>
              <Badge tone={nTareas > 0 ? 'progress' : 'info'}>
                {nTareas > 0 ? `${nTareas} tarea${nTareas === 1 ? '' : 's'}` : 'sin tareas'}
              </Badge>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Lista
                icon={KeyRound}
                titulo="Accesos a revocar"
                n={credPendientes.length}
                vacio="Sin accesos pendientes de revocar."
              >
                {credPendientes.map((c) => (
                  // La clave NO puede ser `c.id`: desde §4.8 hay credenciales DEDUCIDAS, que no
                  // tienen fila y traen `id: null`. Dos deducidas compartían la clave `null` y
                  // React avisaba de hijos duplicados — con el riesgo real de omitir o repetir
                  // filas al re-renderizar. Se usa el recurso, que siempre existe y es único
                  // dentro de la lista por el dedupe de (persona, recurso).
                  <li key={c.id ? `c${c.id}` : `r${c.recurso_id}`}
                    className="flex items-baseline gap-1.5 text-xs text-v25-ink-700">
                    <span className="truncate font-medium">{c.nombre}</span>
                    {c.valor && <span className="truncate text-v25-ink-400">{c.valor}</span>}
                    {/* Aquí también se dice que es una inferencia: quien está creando la baja
                        tiene que poder juzgar la lista que va a convertirse en tareas. */}
                    {c.origen === 'deducida' && (
                      <span className="shrink-0 text-[10px] uppercase tracking-wide text-v25-ink-300">deducida</span>
                    )}
                  </li>
                ))}
              </Lista>

              <div className="space-y-2">
                <Lista
                  icon={Package}
                  titulo="Material a recuperar"
                  n={materialItems.length}
                  vacio={tiene?.material?.fiable
                    ? 'Sin material registrado a su nombre.'
                    : 'Inventario no devuelve nada — ver el aviso.'}
                >
                  {materialItems.map((m) => (
                    <li key={`${m.asset_type_slug}-${m.asset_id}`} className="flex items-baseline gap-1.5 text-xs text-v25-ink-700">
                      <span className="truncate font-medium">{m.tipo_nombre}</span>
                      {m.referencia && <span className="truncate text-v25-ink-400">{m.referencia}</span>}
                      {/* El área que recibe su tarea. Sin ella, la tarea no se reparte a nadie, y
                          eso tiene que verse aquí y no al cerrar. */}
                      {/* El porqué lo manda el servidor (`area_motivo`, 15/10): qué recurso falta y dónde se arregla. */}
                      <span className={`ml-auto shrink-0 text-[10px] ${m.area_nombre ? 'text-v25-ink-400' : 'text-amber-700'}`}
                        title={m.area_nombre ? undefined : (m.area_motivo || 'Su tarea no tendrá área y su aviso no llegará a nadie.')}>
                        {m.area_nombre || 'sin área'}
                      </span>
                    </li>
                  ))}
                </Lista>
                {tiene && !tiene.material.fiable && (
                  <SinFiabilidad motivo={tiene.material.motivo_no_fiable} />
                )}
              </div>
            </div>

            {/* Una baja con cero tareas puede ser una baja limpia o una baja mal informada, y quien
                la crea tiene que poder distinguirlo. Por eso se dice, en vez de no decir nada. */}
            {nTareas === 0 && tiene?.material?.fiable && (
              <p className="mt-3 border-t border-v25-line pt-2.5 text-xs text-v25-ink-500">
                No consta material ni accesos a su nombre, así que la baja nacerá sin tareas.
                Si sabes de algo que no está registrado, añádelo después en el proceso.
              </p>
            )}
          </div>

          <div>
            <QuienSeEntera
              derivadas={derivadas}
              elegidas={elegidasObj}
              correos={correos}
              sinAvisos
              editable
              ocupada={guardando}
              catalogo={catalogoLibre}
              areaAAvisar={areaAAvisar}
              setAreaAAvisar={setAreaAAvisar}
              onAnadirArea={() => { setElegidas((l) => [...l, Number(areaAAvisar)]); setAreaAAvisar(''); }}
              onQuitarArea={(a) => setElegidas((l) => l.filter((id) => id !== Number(a.id)))}
              correoAAvisar={correoAAvisar}
              setCorreoAAvisar={(v) => { setCorreoAAvisar(v); setErrorCorreo(null); }}
              errorCorreo={errorCorreo}
              onAnadirCorreo={anadirCorreo}
              onQuitarCorreo={(c) => setCorreos((l) => l.filter((x) => x !== c))}
            />
            {errorAreas && <p className="mt-1 text-[12px] leading-relaxed text-red-600">{errorAreas}</p>}
            <p className="mt-1.5 text-[11px] leading-relaxed text-v25-ink-400">
              Crear la baja no avisa a nadie todavía: el aviso sale al cerrarla, o antes con «Volver a avisar».
            </p>
          </div>

          {errorGuardar && (
            <div className="flex items-start gap-2 rounded-v25-lg border border-red-200 bg-red-50 px-3 py-2">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
              <p className="text-xs text-red-700">{errorGuardar}</p>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

/**
 * AvisoRevertirBaja — EL SEGUNDO GUARD (§2.bis). Vive aquí, junto al primero, porque los dos
 * guardan la misma cosa desde lados opuestos: que el desplegable de estado y el proceso de salida
 * no se contradigan.
 *
 * Salta cuando alguien saca a una persona de "Baja" teniendo un proceso ABIERTO.
 *
 * **AVISA, NO CANCELA.** Cancelar el proceso como efecto secundario de tocar un desplegable sería
 * deshacer una salida a medias sin que nadie lo pidiera — y el proceso puede llevar ya material
 * recuperado y accesos revocados, que no se "des-revocan". Revertir una salida merece un acto
 * deliberado, y el sitio de ese acto es la pantalla de Bajas.
 */
export function AvisoRevertirBaja({ open, baja, personaNombre, onClose, onIrALaBaja }) {
  if (!baja) return null;
  return (
    <Modal
      open={open}
      onClose={onClose}
      size="sm"
      title="Esta persona tiene una baja abierta"
      subtitle={`${personaNombre || 'La persona'} · proceso ${baja.numero}`}
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Entendido</Button>
          {onIrALaBaja && (
            <Button onClick={() => onIrALaBaja(baja)}><ExternalLink />Ver el proceso</Button>
          )}
        </div>
      }
    >
      <div className="space-y-2 text-xs leading-relaxed text-v25-ink-600">
        <p>
          Has cambiado su estado, pero el proceso <strong className="font-medium">{baja.numero}</strong> sigue
          {baja.estado === 'borrador' ? ' en borrador' : ' en curso'}. <strong className="font-medium">No se
          ha cancelado</strong>: puede llevar material ya recuperado y accesos ya revocados, y eso no se
          deshace solo.
        </p>
        <p>
          Si la salida se ha anulado de verdad, cancela el proceso desde la pestaña de Bajas. Es un
          acto aparte a propósito.
        </p>
      </div>
    </Modal>
  );
}
